import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import type { TargetNode, SeriesCompound } from '../../../../types/targetUniverse';

// Theme-aware colour helpers — read CSS variables at render time
function getC() {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string, fb: string) => s.getPropertyValue(name).trim() || fb;
  return {
    text: v('--text-primary', '#e2e8f0'),
    textMuted: v('--text-secondary', '#64748b'),
    textDim: v('--text-muted', '#475569'),
    panelBorder: v('--card-border', '#1e293b'),
    cardBg: v('--card-bg', '#161b22'),
    projectTarget: '#22d3ee',
    safe: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    accent: '#3b82f6',
  };
}

const HERG_NAMES = new Set(['hERG', 'KCNH2', 'potassium voltage-gated channel subfamily H member 2']);

const heatColor = (val: number | undefined, isHERG: boolean): string => {
  if (val == null) return 'transparent';
  // hERG: high pIC50 = bad (inverse colouring)
  if (isHERG) {
    if (val < 5) return '#10b981';
    if (val < 5.5) return '#f59e0b';
    return '#ef4444';
  }
  // Normal: high pIC50 = good
  if (val > 7.5) return '#059669';
  if (val > 6.5) return '#0d9488';
  if (val > 5.5) return '#f59e0b';
  return '#ef4444';
};

interface Props {
  seriesData: SeriesCompound[];
  currentMoleculeId: number;
  targets: TargetNode[];
}

const MAX_ROWS = 20;
const COL_OPTIONS = [5, 10, 15, 20] as const;

interface SelectedCell {
  moleculeId: number;
  targetName: string;
  pIC50: number | null;
  source: string | null;
  rect: { top: number; left: number };
}

const SeriesHeatmap: React.FC<Props> = ({ seriesData, currentMoleculeId, targets }) => {
  const [colCount, setColCount] = useState<number>(10);
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Follow system theme toggle
  const [, setThemeTick] = useState(0);
  useEffect(() => {
    const observer = new MutationObserver(() => setThemeTick(t => t + 1));
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  const C = getC();

  // Find project target (is_primary)
  const projectTargetName = useMemo(() => {
    const primary = targets.find(t => t.is_primary);
    return primary?.target_name || null;
  }, [targets]);

  // Build lookup: target_name → source
  const targetSourceMap = useMemo(() => {
    const map = new Map<string, string>();
    targets.forEach(t => {
      if (t.source) map.set(t.target_name, t.source);
    });
    return map;
  }, [targets]);

  // Build lookup: molecule_id → target_name → pIC50
  const dataMap = useMemo(() => {
    const map = new Map<number, Map<string, number>>();
    seriesData.forEach(c => {
      const inner = new Map<string, number>();
      c.targets.forEach(t => {
        if (t.pIC50 != null) inner.set(t.target_name, t.pIC50);
      });
      map.set(c.molecule_id, inner);
    });
    return map;
  }, [seriesData]);

  // COLUMNS: Top N targets by highest max pIC50. Project target always first.
  const columns = useMemo(() => {
    const targetMaxPIC50 = new Map<string, number>();
    seriesData.forEach(c => {
      c.targets.forEach(t => {
        if (t.pIC50 != null) {
          const current = targetMaxPIC50.get(t.target_name) || 0;
          if (t.pIC50 > current) targetMaxPIC50.set(t.target_name, t.pIC50);
        }
      });
    });
    targets.forEach(t => {
      if (t.pIC50 != null) {
        const current = targetMaxPIC50.get(t.target_name) || 0;
        if (t.pIC50 > current) targetMaxPIC50.set(t.target_name, t.pIC50);
      }
    });

    const sorted = Array.from(targetMaxPIC50.entries())
      .filter(([name]) => name !== projectTargetName)
      .sort((a, b) => b[1] - a[1])
      .slice(0, colCount - (projectTargetName ? 1 : 0))
      .map(([name]) => name);

    return projectTargetName ? [projectTargetName, ...sorted] : sorted;
  }, [seriesData, targets, projectTargetName, colCount]);

  // ROWS: Top 20 compounds by highest on-target (project target) pIC50
  const rows = useMemo(() => {
    if (seriesData.length <= MAX_ROWS) return seriesData;

    return [...seriesData]
      .sort((a, b) => {
        const aVal = projectTargetName
          ? (a.targets.find(t => t.target_name === projectTargetName)?.pIC50 ?? -Infinity)
          : -Infinity;
        const bVal = projectTargetName
          ? (b.targets.find(t => t.target_name === projectTargetName)?.pIC50 ?? -Infinity)
          : -Infinity;
        return bVal - aVal;
      })
      .slice(0, MAX_ROWS);
  }, [seriesData, projectTargetName]);

  // Dismiss tooltip on Escape or click outside
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') setSelectedCell(null);
  }, []);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      setSelectedCell(null);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleKeyDown, handleClickOutside]);

  const handleCellClick = useCallback((
    e: React.MouseEvent<HTMLDivElement>,
    moleculeId: number,
    targetName: string,
    pIC50: number | null,
  ) => {
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    const top = rect.top - (containerRect?.top ?? 0) + rect.height + 4;
    const left = rect.left - (containerRect?.left ?? 0);
    const source = targetSourceMap.get(targetName) || null;
    console.log('Heatmap cell clicked:', { moleculeId, targetName, pIC50, source });
    setSelectedCell({ moleculeId, targetName, pIC50, source, rect: { top, left } });
  }, [targetSourceMap]);

  if (seriesData.length === 0) {
    return (
      <div style={{
        color: C.textDim, fontSize: 12,
        fontFamily: "'JetBrains Mono', monospace",
        textAlign: 'center', padding: '40px 20px',
      }}>
        No series data available
      </div>
    );
  }

  const cellW = 48;
  const headerH = 60;
  const rowLabelW = 80;

  return (
    <div ref={containerRef} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, position: 'relative' }}>
      {/* Control bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 8 }}>
        <span style={{ color: C.textMuted, fontSize: 10, marginRight: 4 }}>Targets:</span>
        {COL_OPTIONS.map(n => (
          <button
            key={n}
            onClick={() => { setColCount(n); setSelectedCell(null); }}
            style={{
              padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
              fontFamily: "'JetBrains Mono', monospace",
              border: n === colCount ? `1px solid ${C.accent}` : `1px solid ${C.panelBorder}`,
              background: n === colCount ? 'rgba(59,130,246,0.15)' : 'transparent',
              color: n === colCount ? C.accent : C.textMuted,
              cursor: 'pointer',
            }}
          >
            Top {n}
          </button>
        ))}
      </div>

      <div style={{ color: C.textMuted, fontSize: 11, marginBottom: 8, textAlign: 'center' }}>
        {seriesData.length > MAX_ROWS ? `Top ${MAX_ROWS} of ${seriesData.length}` : `${seriesData.length}`} compounds {'\u00D7'}{' '}
        {columns.length} targets (pIC50)
      </div>
      <div style={{ overflowX: 'auto' }}>
        {/* Rotated column headers */}
        <div style={{ display: 'flex', paddingLeft: rowLabelW, height: headerH, alignItems: 'flex-end' }}>
          {columns.map(t => {
            const isHerg = HERG_NAMES.has(t);
            const isProject = t === projectTargetName;
            return (
              <div key={t} style={{
                width: cellW, position: 'relative', height: headerH,
              }}>
                <div style={{
                  position: 'absolute', bottom: 4, left: cellW / 2,
                  transformOrigin: 'bottom left',
                  transform: 'rotate(-45deg)',
                  whiteSpace: 'nowrap',
                  color: isProject ? C.projectTarget : isHerg ? C.danger : C.textMuted,
                  fontSize: 9, fontWeight: isProject ? 700 : 400,
                }} title={t}>
                  {t.length > 18 ? t.substring(0, 15) + '...' : t}
                </div>
              </div>
            );
          })}
        </div>
        {/* Rows */}
        {rows.map(compound => {
          const isCurrent = compound.molecule_id === currentMoleculeId;
          const targetMap = dataMap.get(compound.molecule_id);
          return (
            <div key={compound.molecule_id} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
              <div style={{
                width: rowLabelW, color: isCurrent ? C.projectTarget : C.text,
                fontSize: 12, paddingRight: 8, textAlign: 'right',
                fontWeight: isCurrent ? 700 : 500,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }} title={compound.smiles}>
                {isCurrent ? '\u25B8 current' : `#${compound.molecule_id}`}
              </div>
              {columns.map(col => {
                const val = targetMap?.get(col);
                const isHerg = HERG_NAMES.has(col);
                const bg = heatColor(val, isHerg);
                const isSelected = selectedCell?.moleculeId === compound.molecule_id && selectedCell?.targetName === col;
                return (
                  <div
                    key={col}
                    onClick={(e) => handleCellClick(e, compound.molecule_id, col, val ?? null)}
                    style={{
                      width: cellW, height: 28,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: bg,
                      borderRadius: 3, margin: '0 1px',
                      color: val != null ? '#fff' : C.textDim,
                      fontSize: 10, fontWeight: val != null ? 600 : 400,
                      border: isSelected ? `2px solid ${C.accent}` : val == null ? `1px solid ${C.panelBorder}` : 'none',
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                    }}
                  >
                    {val != null ? val.toFixed(1) : '\u2014'}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Tooltip popover */}
      {selectedCell && (
        <div style={{
          position: 'absolute',
          top: selectedCell.rect.top,
          left: selectedCell.rect.left,
          background: C.cardBg,
          color: C.text,
          borderRadius: 6,
          padding: '8px 12px',
          fontSize: 11,
          fontFamily: "'JetBrains Mono', monospace",
          zIndex: 100,
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          border: `1px solid ${C.accent}`,
          minWidth: 160,
          pointerEvents: 'none',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4, color: C.accent }}>{selectedCell.targetName}</div>
          <div style={{ marginBottom: 2 }}>Compound: <span style={{ color: C.text }}>#{selectedCell.moleculeId}</span></div>
          <div style={{ marginBottom: 2 }}>pIC50: <span style={{ color: selectedCell.pIC50 != null ? '#fff' : C.textDim, fontWeight: 600 }}>
            {selectedCell.pIC50 != null ? selectedCell.pIC50.toFixed(2) : 'N/A'}
          </span></div>
          {selectedCell.source && (
            <div>Source: <span style={{ color: C.textMuted }}>{selectedCell.source}</span></div>
          )}
        </div>
      )}

      <div style={{ color: C.textDim, fontSize: 10, marginTop: 12, textAlign: 'center' }}>
        Selectivity = IC50(worst off-target) / IC50(primary)
      </div>
    </div>
  );
};

export default SeriesHeatmap;
